import React, { useState, useEffect, useContext } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Container, createMuiTheme, Toolbar, Fab, Typography, CssBaseline, Tooltip, Snackbar, FormControl, InputLabel, Select, Divider, IconButton, AppBar } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import MuiAlert from '@material-ui/lab/Alert';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import ExportIcon from '@material-ui/icons/GetApp';
import 'date-fns';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import { HttpService } from '../../apiAuthorization/httpService';
import { GlobalTheme } from '../../layouts/theme';
import { Context } from '../contexts/siteContext';
import { Modules } from '../contexts/modulesContext';
import { Theme } from '../contexts/themeContext';
import Card from '../card/card.js';
import CardBody from '../card/cardBody.js';
import SmallMenu from '../header/smallMenu';
import StatusIcon from '../common/statusIcon';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            margin: '7px 0px',
            width: '100%'
        }
    },
    button: {
        width: '15%',
        float: 'right'
    },
    error: {
        fontSize: '12px',
        fontWeight: "bold",
        color: 'red',
        marginTop: '-5px'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    title: {
        flex: 1
    }
}));

export default function POSUnitBreakoutReport(props) {
    const classes = useStyles();
    const { defaultSite, sites } = useContext(Context);
    const { trainingMode } = useContext(Modules);
    const { theme } = useContext(Theme);
    const [sitesList, setSitesList] = useState([]);
    const [intervals, setIntervals] = useState([]);
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);
    const [transactionLog, setTransactionLog] = useState([]);
    const [date, setDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite));
    const [startDate, setStartDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite));
    const [endDate, setEndDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", 24, defaultSite));
    const [siteId, setSiteId] = useState(0);
    const [interval, setInterval] = useState("Daily");
    const [month, setMonth] = useState("");
    const [year, setYear] = useState(0);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [backDrop, setBackDrop] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const currentTheme = theme ? createMuiTheme(GlobalTheme[theme]) : createMuiTheme(GlobalTheme[localStorage.getItem("theme")]);
    const tableHeight = window.innerHeight - 315;

    const [unitBreakouts, setUnitBreakouts] = useState({
        columns: [
            { field: 'UnitName', title: 'Units' },
            { field: 'TotalPOS', title: 'Total Observation Assigned' },
            { field: 'Week1', title: 'Week 1 Hours', hidden: true, render: rowData => rowData.Week1.toFixed(2) },
            { field: 'Week2', title: 'Week 2 Hours', hidden: true, render: rowData => rowData.Week2.toFixed(2) },
            { field: 'Week3', title: 'Week 3 Hours', hidden: true, render: rowData => rowData.Week3.toFixed(2) },
            { field: 'Week4', title: 'Week 4 Hours', hidden: true, render: rowData => rowData.Week4.toFixed(2) },
            { field: 'POSHours', title: 'Total Hours', render: rowData => rowData.POSHours.toFixed(2) }
        ],
        data: []
    });

    const triggerSave = () => {
        updateAppTransactionLog();
        props.history.push({ pathname: "/" });
    };

    const rowStyle = (rowData, rowIndex) => {
        if (rowData.UnitName === "Total") {
            return { backgroundColor: "grey", color: "white", fontWeight: "Bold" };
        }
        else {
            return { backgroundColor: rowIndex % 2 === 0 ? '' : currentTheme.alternateGridRow };
        }
    };

    const closeSuccessSnackbar = () => {
        setOpenSuccess(false);
    };

    const enableWeekStartDates = (date) => {
        return date.getDay() !== 0;
    }

    const enableWeekEndDates = (date) => {
        return date.getDay() !== 6;
    }

    const getUnitBreakouts = () => {
        let model = {};
        let totalWeeks = 0;

        if (interval === "Weekly") {
            totalWeeks = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) / 6;
            if (totalWeeks > 5) {
                setSuccessMessage("Total Weeks selected can not be more than 4. Please change selection of dates.");
                setOpenSuccess(true);
                return;
            }
        }

        model.SiteId = siteId;
        model.Interval = interval;
        model.DailyDate = date;
        model.FilterStartDate = startDate;
        model.FilterEndDate = endDate;
        model.Month = month;
        model.Year = year;
        model.TrainingMode = trainingMode;
        model.AccessibleSites = sitesList;

        setBackDrop(true);
        HttpService.post("/PatientSitting/GetPOSUnitBreakoutReport", model)
            .then((result) => {
                let data = result.data;
                let totalObservation = 0, totalHours = 0, totalWeek1 = 0, totalWeek2 = 0, totalWeek3 = 0, totalWeek4 = 0;

                data.forEach(item => {
                    if (item.Interval === "Weekly") {
                        totalWeek1 += item.Week1;
                        totalWeek2 += item.Week2;
                        totalWeek3 += item.Week3;
                        totalWeek4 += item.Week4;
                     }
                    totalObservation += item.TotalPOS;
                    totalHours += item.POSHours;
                });

                let columns = unitBreakouts.columns;
                if (interval === "Weekly") {
                    columns[2].hidden = totalWeeks >= 1 ? false : true;
                    columns[3].hidden = totalWeeks >= 2 ? false : true;
                    columns[4].hidden = totalWeeks >= 3 ? false : true;
                    columns[5].hidden = totalWeeks >= 4 ? false : true;
                }
                else {
                    columns[2].hidden = true;
                    columns[3].hidden = true;
                    columns[4].hidden = true;
                    columns[5].hidden = true;
                }
                
                if (data.length > 0)
                    data.push({ UnitName: "Total", TotalPOS: totalObservation, Week1: totalWeek1, Week2: totalWeek2, Week3: totalWeek3, Week4: totalWeek4, POSHours: totalHours });
                setUnitBreakouts({ ...unitBreakouts, data: data });
                setBackDrop(false);
            },
                (error) => {
                    console.log(error);
                });
    };

    const exportToExcel = () => {
        let model = {};

        if (interval === "Weekly") {
            let totalWeeks = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24) / 6;
            if (totalWeeks > 5) {
                setSuccessMessage("Total Weeks selected can not be more than 4. Please change selection of dates.");
                setOpenSuccess(true);
                return;
            }
        }

        model.SiteId = siteId;
        model.Interval = interval;
        model.DailyDate = date;
        model.FilterStartDate = startDate;
        model.FilterEndDate = endDate;
        model.Month = month;
        model.Year = year;
        model.TrainingMode = trainingMode;
        model.AccessibleSites = sitesList;

        setBackDrop(true);
        HttpService.post("/PatientSitting/POSUnitBreakoutReporExport", model)
            .then((result) => {
                if (result.success) {
                    addAppTransactionLog("POS Unit Breakout Member - Print", "Print");
                    let fileName = result.name;
                    HttpService.downloadFile('/Report/DownloadFile', { fileName: fileName })
                        .then((response) => {
                            const url = window.URL.createObjectURL(new Blob([response]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                        });
                }
                else {
                    setSuccessMessage(result.message);
                    setOpenSuccess(true);
                }
                setBackDrop(false);
            },
                (error) => {
                    console.log(error);
                });
    };

    const addAppTransactionLog = (reportName, actionType) => {
        HttpService.post("/AppTransactionLog/SaveAppTransactionLog", { ReportName: reportName, ActionType: actionType })
            .then((result) => {
                if (actionType === "Access") {
                    setTransactionLog(result.data);
                }
            },
                (error) => {
                    console.log(error);
                });
    };

    const updateAppTransactionLog = () => {
        HttpService.post("/AppTransactionLog/UpdateAppTransactionLog", transactionLog)
            .then((result) => {
            },
                (error) => {
                    console.log(error);
                });
    };

    useEffect(() => {
        if (sitesList.length > 0) {
            getUnitBreakouts();
        }
        // eslint-disable-next-line 
    }, [siteId, interval, date, startDate, endDate, month, year]);

    useEffect(() => {
        let data = sites.slice();
        let index = sites.findIndex(item => item.id === 0);
        if (index === -1) {
            data.unshift({ id: 0, name: "All" });
            setSitesList(data);
        }
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        let year = new Date().getFullYear();
        let startOfWeek = moment().startOf('isoweek').toDate();
        let endOfWeek = moment().endOf('week').toDate();

        setIntervals(["Daily", "Weekly", "Monthly"]);
        setMonths(monthNames);
        setYears([year - 1, year, year + 1]);
        setStartDate(ConvertToCurrentTimeZone(startOfWeek, "L", null, defaultSite));
        setEndDate(ConvertToCurrentTimeZone(endOfWeek, "L", null, defaultSite));
        setMonth(monthNames[new Date().getMonth()]);
        setYear(year);
        addAppTransactionLog("POS Unit Breakout Member - Access", "Access");
        // eslint-disable-next-line 
    }, []);

    useEffect(() => {
        setSiteId(defaultSite.id);
        // eslint-disable-next-line 
    }, [sitesList]);

    return (
        <React.Fragment>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <Backdrop className={classes.backdrop} id="posUnitBreakoutBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Snackbar open={openSuccess} autoHideDuration={3000} onClose={closeSuccessSnackbar} style={{ height: "100%" }}>
                    <Alert onClose={closeSuccessSnackbar} severity="success">
                        {successMessage}
                    </Alert>
                </Snackbar>
                <Container maxWidth="xl" height="100%" className={props.styles}>
                    <AppBar style={{ background: currentTheme.primary, color: currentTheme.color }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={triggerSave} aria-label="back">
                                <KeyboardBackspaceIcon />
                            </IconButton>
                            <Grid container direction="row" alignItems="center">
                                <Grid item>
                                    <Typography variant="h6" className={classes.title}>POS Unit Breakout Report</Typography>
                                </Grid>
                                <Grid item>
                                    <StatusIcon />
                                </Grid>
                            </Grid>
                            <Grid container justify="flex-end">
                                {
                                    (props.location.settingMenus.indexOf("POS Unit Breakout Member - Print") > -1) &&
                                    <Fab style={{ background: currentTheme.secondary, color: "white" }} size="small" aria-label="export" onClick={exportToExcel}>
                                        <Tooltip title="Export" arrow><ExportIcon /></Tooltip>
                                    </Fab>
                                }
                                <SmallMenu modified={false} />
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <Card color="primary" style={{ height: "100%", background: currentTheme.background }}>
                        <CardBody style={{ color: currentTheme.text, marginTop: "50px" }}>
                            <div className={classes.root} autoComplete="off">
                                <FormControl variant="outlined" margin="dense" className={classes.ddl}>
                                    <InputLabel htmlFor="Site">Site</InputLabel>
                                    <Select
                                        native
                                        label="Site"
                                        size="small"
                                        value={siteId}
                                        onChange={(e) => { setSiteId(parseInt(e.target.value)); }}
                                    >
                                        {
                                            sitesList.map((option, key) => {
                                                return <option key={option.id} value={option.id} label={option.name} >{option.name}</option>;
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                <FormControl variant="outlined" margin="dense" className={classes.ddl}>
                                    <InputLabel htmlFor="Interval">Interval</InputLabel>
                                    <Select
                                        native
                                        label="Interval"
                                        size="small"
                                        value={interval}
                                        onChange={(e) => { setInterval(e.target.value); }}
                                    >
                                        {
                                            intervals.map((option, key) => {
                                                return <option key={option} value={option} label={option} >{option}</option>;
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {
                                    (interval === "Daily") &&
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <KeyboardDatePicker disableToolbar fullWidth autoOk
                                            variant="inline"
                                            inputVariant="outlined"
                                            id="Date"
                                            label="Date"
                                            format="MM/dd/yyyy"
                                            size="small"
                                            value={date}
                                            onChange={date => setDate(moment(date).format("MM/DD/YYYY"))}
                                            InputAdornmentProps={{ position: "start" }}
                                            PopoverProps={{
                                                anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                                transformOrigin: { horizontal: "left", vertical: "top" }
                                            }}
                                        />
                                    </MuiPickersUtilsProvider>
                                }
                                {
                                    (interval === "Weekly") &&
                                    <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <KeyboardDatePicker disableToolbar fullWidth autoOk
                                                    variant="inline"
                                                    inputVariant="outlined"
                                                    id="StartDate"
                                                    label="Start Date"
                                                    format="MM/dd/yyyy"
                                                    size="small"
                                                    value={startDate}
                                                    onChange={date => setStartDate(moment(date).format("MM/DD/YYYY"))}
                                                    shouldDisableDate={enableWeekStartDates}
                                                    InputAdornmentProps={{ position: "start" }}
                                                    PopoverProps={{
                                                        anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                                        transformOrigin: { horizontal: "left", vertical: "top" }
                                                    }}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <KeyboardDatePicker disableToolbar fullWidth autoOk
                                                    variant="inline"
                                                    inputVariant="outlined"
                                                    id="EndDate"
                                                    label="End Date"
                                                    format="MM/dd/yyyy"
                                                    size="small"
                                                    minDate={startDate}
                                                    value={endDate}
                                                    onChange={date => setEndDate(moment(date).format("MM/DD/YYYY"))}
                                                    shouldDisableDate={enableWeekEndDates}
                                                    InputAdornmentProps={{ position: "start" }}
                                                    PopoverProps={{
                                                        anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                                        transformOrigin: { horizontal: "left", vertical: "top" }
                                                    }}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>
                                }
                                {
                                    (interval === "Monthly") &&
                                    <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth variant="outlined" margin="dense" className={classes.ddl}>
                                                <InputLabel htmlFor="Month">Month</InputLabel>
                                                <Select
                                                    native
                                                    label="Month"
                                                    size="small"
                                                    value={month}
                                                    onChange={(e) => { setMonth(e.target.value); }}
                                                >
                                                    {
                                                        months.map((option, key) => {
                                                            return <option key={option} value={option} label={option} >{option}</option>;
                                                        })
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth variant="outlined" margin="dense" className={classes.ddl}>
                                                <InputLabel htmlFor="Year">Year</InputLabel>
                                                <Select
                                                    native
                                                    label="Year"
                                                    size="small"
                                                    value={year}
                                                    onChange={(e) => { setYear(parseInt(e.target.value)); }}
                                                >
                                                    {
                                                        years.map((option, key) => {
                                                            return <option key={option} value={option} label={option} >{option}</option>;
                                                        })
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                }
                            </div>
                            <Divider style={{ height: "1px", backgroundColor: currentTheme.secondary, marginTop: "5px", marginBottom: "10px" }} />
                            <div style={{ maxWidth: '100%' }}>
                                <MaterialTable
                                    columns={unitBreakouts.columns}
                                    data={unitBreakouts.data}
                                    components={{
                                        Toolbar: (props) => (
                                            <div style={{ height: "0px" }}>
                                                <MTableToolbar {...props} />
                                            </div>
                                        )
                                    }}
                                    style={{ backgroundColor: currentTheme.background, border: "1px solid white" }}
                                    options={{
                                        filtering: false,
                                        search: false,
                                        paging: false,
                                        actionsColumnIndex: -1,
                                        minBodyHeight: tableHeight,
                                        maxBodyHeight: tableHeight,
                                        headerStyle: { fontWeight: "bold", color: currentTheme.headerColor, backgroundColor: currentTheme.headerBackground },
                                        actionsCellStyle: { color: currentTheme.secondary, hover: "white" },
                                        rowStyle: rowStyle
                                    }}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </ThemeProvider>
        </React.Fragment>
    )
}
